import React, { useState, useEffect, useCallback } from 'react';
import { Send, Users, Eye, Loader2, CheckCircle, History, Mail, AlertCircle, BarChart3, MousePointerClick, MailOpen, ChevronDown, ChevronUp, FlaskConical, Save, BookOpen, Trash2, Timer, Play, Pause, Plus, X, Monitor, Tablet, Smartphone, PanelLeftClose, PanelRightClose, Upload, Clock, Calendar as CalIcon, Ban, Copy, Sparkles } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { toast } from 'sonner';
import axios from 'axios';
import EmailBlockEditor, { blocksToHtml, templateToBlocks, useBlockHistory } from './EmailBlockEditor';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FILTER_OPTIONS = [
  { id: 'recent_quotes', label: 'Quote Requestors', desc: 'Customers who submitted a quote' },
  { id: 'all_customers', label: 'All Customers', desc: 'Quotes + orders + customer records' },
  { id: 'custom_list', label: 'Custom List', desc: 'Manually entered email addresses' },
];

const BUILT_IN_TEMPLATES = [
  { id: 'seasonal', name: 'Seasonal Promotion', subject: 'Apollo Shades — Exclusive Spring Savings Inside' },
  { id: 'followup', name: 'Quote Follow-Up', subject: 'Apollo Shades — Still thinking about those shades?' },
];

/* ─── Campaign Stats ─── */
function CampaignStats({ campaignId, token }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const fetchStats = async () => {
    if (stats) { setExpanded(e => !e); return; }
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/email-campaigns/${campaignId}/stats`, { headers: { Authorization: `Bearer ${token}` } });
      setStats(res.data);
      setExpanded(true);
    } catch { toast.error('Failed to load stats'); }
    finally { setLoading(false); }
  };

  return (
    <div>
      <button onClick={fetchStats} className="flex items-center gap-1 text-xs text-amber-600 hover:text-amber-800 transition-colors" data-testid={`view-stats-${campaignId}`}>
        {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <BarChart3 className="w-3 h-3" />}
        {expanded ? 'Hide' : 'Stats'}
        {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
      </button>
      {expanded && stats && (
        <div className="mt-2 grid grid-cols-4 gap-2 text-center" data-testid={`stats-panel-${campaignId}`}>
          <div className="bg-white border rounded-lg p-2">
            <p className="text-lg font-bold text-neutral-800">{stats.total_sent}</p>
            <p className="text-[10px] text-neutral-400 uppercase">Sent</p>
          </div>
          <div className="bg-white border rounded-lg p-2">
            <div className="flex items-center justify-center gap-1"><MailOpen className="w-3.5 h-3.5 text-blue-500" /><p className="text-lg font-bold text-blue-600">{stats.open_rate}%</p></div>
            <p className="text-[10px] text-neutral-400 uppercase">{stats.unique_opens} opens</p>
          </div>
          <div className="bg-white border rounded-lg p-2">
            <div className="flex items-center justify-center gap-1"><MousePointerClick className="w-3.5 h-3.5 text-green-500" /><p className="text-lg font-bold text-green-600">{stats.click_rate}%</p></div>
            <p className="text-[10px] text-neutral-400 uppercase">{stats.unique_clicks} clicks</p>
          </div>
          <div className="bg-white border rounded-lg p-2">
            <p className="text-lg font-bold text-neutral-800">{stats.total_opens + stats.total_clicks}</p>
            <p className="text-[10px] text-neutral-400 uppercase">Total events</p>
          </div>
          {stats.link_stats?.length > 0 && (
            <div className="col-span-4 text-left bg-white border rounded-lg p-2">
              <p className="text-[10px] text-neutral-400 uppercase mb-1">Top Links</p>
              {stats.link_stats.slice(0, 5).map((ls, i) => (
                <div key={i} className="flex items-center justify-between text-xs py-0.5">
                  <span className="text-neutral-600 truncate max-w-[200px]">{ls.url || ls.link_id}</span>
                  <span className="text-neutral-400">{ls.clicks} ({ls.unique_clicks} unique)</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ─── A/B Test Card ─── */
function ABTestCard({ test, token, onRefresh }) {
  const [resolving, setResolving] = useState(false);
  const resolve = async () => {
    if (!confirm('Pick the winner and send to the remaining audience?')) return;
    setResolving(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/email-campaigns/ab-test/${test.id}/resolve`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Winner: Subject ${res.data.winner.toUpperCase()} — sent to ${res.data.remainder_sent} more recipients`);
      onRefresh?.();
    } catch { toast.error('Failed to resolve A/B test'); }
    finally { setResolving(false); }
  };

  return (
    <div className="p-3 bg-purple-50 rounded-lg border border-purple-200" data-testid={`ab-test-${test.id}`}>
      <div className="flex items-center gap-2 mb-2">
        <FlaskConical className="w-4 h-4 text-purple-600" />
        <span className="text-sm font-medium text-purple-800">A/B Test</span>
        <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${test.phase === 'completed' ? 'bg-green-100 text-green-700' : 'bg-purple-100 text-purple-700'}`}>
          {test.phase}
        </span>
      </div>
      <div className="grid grid-cols-2 gap-2 text-xs">
        <div className={`p-2 rounded border ${test.winner === 'a' ? 'bg-green-50 border-green-300' : 'bg-white'}`}>
          <p className="font-medium text-neutral-700">A: {test.subject_a}</p>
          <p className="text-neutral-400">Sent: {test.sent_a} {test.opens_a !== undefined ? `· Opens: ${test.opens_a}` : ''}</p>
        </div>
        <div className={`p-2 rounded border ${test.winner === 'b' ? 'bg-green-50 border-green-300' : 'bg-white'}`}>
          <p className="font-medium text-neutral-700">B: {test.subject_b}</p>
          <p className="text-neutral-400">Sent: {test.sent_b} {test.opens_b !== undefined ? `· Opens: ${test.opens_b}` : ''}</p>
        </div>
      </div>
      {test.phase === 'testing' && (
        <Button size="sm" variant="outline" onClick={resolve} disabled={resolving} className="mt-2 text-purple-700 border-purple-300" data-testid="resolve-ab-test">
          {resolving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <CheckCircle className="w-3 h-3 mr-1" />}
          Pick Winner & Send to Rest ({test.remainder?.length || 0})
        </Button>
      )}
      {test.winner && <p className="text-xs text-green-600 mt-1 font-medium">Winner: Subject {test.winner.toUpperCase()} — {test.winner_sent || 0} sent to remainder</p>}
    </div>
  );
}

/* ─── Main CampaignsTab ─── */
export const CampaignsTab = ({ token }) => {
  const [subject, setSubject] = useState('');
  const { blocks, setBlocks, undo, redo, canUndo, canRedo, resetHistory } = useBlockHistory(() => templateToBlocks('seasonal'));
  const [daysBack, setDaysBack] = useState(90);
  const [filterType, setFilterType] = useState('recent_quotes');
  const [sending, setSending] = useState(false);
  const [previewCount, setPreviewCount] = useState(null);
  const [previewRecipients, setPreviewRecipients] = useState([]);
  const [showRecipients, setShowRecipients] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [showHtmlPreview, setShowHtmlPreview] = useState(false);
  const [editorMode, setEditorMode] = useState('visual');
  const [rawHtml, setRawHtml] = useState('');

  // A/B testing
  const [abMode, setAbMode] = useState(false);
  const [subjectB, setSubjectB] = useState('');
  const [testPct, setTestPct] = useState(20);

  // Template library
  const [savedTemplates, setSavedTemplates] = useState([]);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);

  // Follow-up sequence
  const [activeView, setActiveView] = useState('compose'); // compose | templates | followup
  const [previewDevice, setPreviewDevice] = useState('desktop'); // mobile | tablet | desktop
  const [showPreview, setShowPreview] = useState(true);
  // Split ratio: % of total width for compose panel (saved to localStorage)
  const [splitPct, setSplitPct] = useState(() => {
    try { return parseInt(localStorage.getItem('apollo_compose_split')) || 55; }
    catch { return 55; }
  });
  const splitContainerRef = React.useRef(null);
  const isDraggingSplit = React.useRef(false);
  // Editor height saved separately
  const [editorHeight, setEditorHeight] = useState(() => {
    try { return parseInt(localStorage.getItem('apollo_editor_height')) || 350; }
    catch { return 350; }
  });
  const editorRef = React.useRef(null);
  const [followUpSeq, setFollowUpSeq] = useState(null);
  const [followUpLog, setFollowUpLog] = useState([]);
  const [savingSequence, setSavingSequence] = useState(false);

  // Custom audience
  const [customEmails, setCustomEmails] = useState([]);
  const [emailInput, setEmailInput] = useState('');
  const [savedAudiences, setSavedAudiences] = useState([]);
  const [selectedAudienceId, setSelectedAudienceId] = useState(null);
  const [audienceName, setAudienceName] = useState('');
  const [showAudienceSave, setShowAudienceSave] = useState(false);

  // Schedule & merge
  const [scheduleMode, setScheduleMode] = useState(false);
  const [scheduleDatetime, setScheduleDatetime] = useState('');
  const [mergeEnabled, setMergeEnabled] = useState(false);
  const [mergeFilters, setMergeFilters] = useState([]);

  // Send time optimization
  const [sendTimeSuggestion, setSendTimeSuggestion] = useState(null);
  const [loadingSendTime, setLoadingSendTime] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchAll = useCallback(async () => {
    try {
      const [campRes, tplRes, seqRes, logRes, audRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/email-campaigns`, { headers }),
        axios.get(`${API_URL}/api/admin/email-templates`, { headers }),
        axios.get(`${API_URL}/api/admin/follow-up-sequences`, { headers }),
        axios.get(`${API_URL}/api/admin/follow-up-sequences/log`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/admin/custom-audiences`, { headers }).catch(() => ({ data: [] })),
      ]);
      setCampaigns(campRes.data || []);
      setSavedTemplates(tplRes.data || []);
      setFollowUpSeq(seqRes.data?.id ? seqRes.data : null);
      setFollowUpLog(logRes.data || []);
      setSavedAudiences(audRes.data || []);
    } catch { /* ignore */ }
  }, [token]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Send time optimization
  const fetchBestSendTime = async () => {
    setLoadingSendTime(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/email-campaigns/best-send-time`, { headers });
      setSendTimeSuggestion(res.data);
    } catch { toast.error('Failed to fetch send time suggestion'); }
    finally { setLoadingSendTime(false); }
  };

  // Campaign duplicator
  const duplicateCampaign = async (campaignId) => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/email-campaigns/${campaignId}/duplicate-data`, { headers });
      const data = res.data;
      setSubject(data.subject ? `Copy of ${data.subject}` : '');
      if (data.html_content) {
        setRawHtml(data.html_content);
        setEditorMode('html');
      }
      if (data.filter_type) setFilterType(data.filter_type);
      if (data.days_back) setDaysBack(data.days_back);
      if (data.custom_emails?.length) setCustomEmails(data.custom_emails);
      if (data.merge_filters?.length) {
        setMergeEnabled(true);
        setMergeFilters(data.merge_filters);
      }
      setActiveView('compose');
      setPreviewCount(null);
      setShowRecipients(false);
      toast.success('Campaign duplicated — edit and send!');
    } catch { toast.error('Failed to duplicate campaign'); }
  };

  // Persist editor height via ResizeObserver
  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    const observer = new ResizeObserver(entries => {
      for (const entry of entries) {
        const h = Math.round(entry.contentRect.height);
        if (h > 50) {
          localStorage.setItem('apollo_editor_height', String(h));
        }
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [activeView]);

  // Split divider drag handler
  const handleSplitDragStart = useCallback((e) => {
    e.preventDefault();
    isDraggingSplit.current = true;
    const container = splitContainerRef.current;
    if (!container) return;

    const onMove = (moveEvent) => {
      if (!isDraggingSplit.current) return;
      const rect = container.getBoundingClientRect();
      const x = (moveEvent.clientX || moveEvent.touches?.[0]?.clientX) - rect.left;
      const pct = Math.min(80, Math.max(30, Math.round((x / rect.width) * 100)));
      setSplitPct(pct);
    };
    const onUp = () => {
      isDraggingSplit.current = false;
      localStorage.setItem('apollo_compose_split', String(splitPct));
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    document.addEventListener('touchmove', onMove);
    document.addEventListener('touchend', onUp);
  }, [splitPct]);

  const getHtml = () => editorMode === 'visual' ? blocksToHtml(blocks) : rawHtml;

  const loadTemplate = (tpl) => {
    if (tpl.blocks?.length) {
      resetHistory(tpl.blocks);
      setEditorMode('visual');
    } else if (tpl.id && ['seasonal', 'followup'].includes(tpl.id)) {
      resetHistory(templateToBlocks(tpl.id));
      setEditorMode('visual');
    } else if (tpl.html_content) {
      setRawHtml(tpl.html_content);
      setEditorMode('html');
    }
    setSubject(tpl.subject);
    toast.success(`Loaded "${tpl.name}"`);
  };

  const previewAudience = async () => {
    try {
      setSending(true);
      const payload = { subject: subject || 'Preview', html_content: getHtml() || '<p>Preview</p>', days_back: daysBack, filter_type: filterType, test_only: true };
      if (filterType === 'custom_list' || mergeEnabled) payload.custom_emails = customEmails;
      if (mergeEnabled && mergeFilters.length) payload.merge_filters = mergeFilters;
      const res = await axios.post(`${API_URL}/api/admin/email-campaigns/send`, payload, { headers });
      setPreviewCount(res.data.count);
      setPreviewRecipients(res.data.recipients || []);
      setShowRecipients(true);
    } catch { toast.error('Failed to preview audience'); }
    finally { setSending(false); }
  };

  // Custom audience helpers
  const addEmails = (raw) => {
    const parsed = raw.split(/[,;\n\s]+/).map(e => e.trim().toLowerCase()).filter(e => e && e.includes('@'));
    if (!parsed.length) return;
    setCustomEmails(prev => [...new Set([...prev, ...parsed])]);
    setEmailInput('');
  };

  const removeEmail = (email) => setCustomEmails(prev => prev.filter(e => e !== email));

  const loadAudience = (audience) => {
    setCustomEmails(audience.emails || []);
    setSelectedAudienceId(audience.id);
    setAudienceName(audience.name);
    toast.success(`Loaded "${audience.name}"`);
  };

  const saveAudience = async () => {
    if (!audienceName.trim() || !customEmails.length) { toast.error('Name and at least one email required'); return; }
    try {
      if (selectedAudienceId) {
        await axios.put(`${API_URL}/api/admin/custom-audiences/${selectedAudienceId}`, { name: audienceName, emails: customEmails }, { headers });
        toast.success('Audience updated');
      } else {
        const res = await axios.post(`${API_URL}/api/admin/custom-audiences`, { name: audienceName, emails: customEmails }, { headers });
        setSelectedAudienceId(res.data.id);
        toast.success('Audience saved');
      }
      setShowAudienceSave(false);
      fetchAll();
    } catch { toast.error('Failed to save audience'); }
  };

  const deleteAudience = async (id) => {
    if (!confirm('Delete this saved audience?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/custom-audiences/${id}`, { headers });
      if (selectedAudienceId === id) { setSelectedAudienceId(null); setAudienceName(''); }
      toast.success('Audience deleted');
      fetchAll();
    } catch { toast.error('Failed to delete'); }
  };

  const csvFileRef = React.useRef(null);
  const handleCsvImport = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      const text = evt.target.result;
      const emailRegex = /[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}/g;
      const found = text.match(emailRegex) || [];
      const unique = [...new Set(found.map(e => e.toLowerCase().trim()))];
      if (!unique.length) { toast.error('No valid emails found in file'); return; }
      setCustomEmails(prev => [...new Set([...prev, ...unique])]);
      toast.success(`Imported ${unique.length} email${unique.length !== 1 ? 's' : ''} from CSV`);
    };
    reader.readAsText(file);
    e.target.value = ''; // reset so same file can be re-imported
  };

  const buildPayload = (extra = {}) => {
    const payload = { subject, html_content: getHtml(), days_back: daysBack, filter_type: filterType, ...extra };
    if (filterType === 'custom_list' || mergeEnabled) payload.custom_emails = customEmails;
    if (mergeEnabled && mergeFilters.length) payload.merge_filters = mergeFilters;
    if (scheduleMode && scheduleDatetime) payload.scheduled_at = new Date(scheduleDatetime).toISOString();
    return payload;
  };

  const sendPreview = async () => {
    const html = getHtml();
    if (!subject || !html) { toast.error('Fill in subject and content'); return; }
    try {
      setSending(true);
      const payload = buildPayload({ test_email: 'delivered@resend.dev' });
      delete payload.scheduled_at; // previews always send immediately
      await axios.post(`${API_URL}/api/admin/email-campaigns/send`, payload, { headers });
      toast.success('Preview sent to delivered@resend.dev');
    } catch { toast.error('Failed to send preview'); }
    finally { setSending(false); }
  };

  const sendCampaign = async () => {
    const html = getHtml();
    if (!subject || !html) { toast.error('Fill in subject and content'); return; }
    if (filterType === 'custom_list' && !mergeEnabled && !customEmails.length) { toast.error('Add at least one email address'); return; }
    if (scheduleMode && !scheduleDatetime) { toast.error('Pick a date and time to schedule'); return; }

    if (abMode) {
      if (!subjectB) { toast.error('Fill in Subject B for A/B test'); return; }
      if (!confirm(`Run A/B test: ${testPct}% test group, then send winner to the rest?`)) return;
      try {
        setSending(true);
        const payload = {
          subject_a: subject, subject_b: subjectB, html_content: html,
          days_back: daysBack, filter_type: filterType, test_pct: testPct
        };
        if (filterType === 'custom_list' || mergeEnabled) payload.custom_emails = customEmails;
        if (mergeEnabled && mergeFilters.length) payload.merge_filters = mergeFilters;
        const res = await axios.post(`${API_URL}/api/admin/email-campaigns/ab-test`, payload, { headers });
        toast.success(`A/B test started! Group A: ${res.data.sent_a}, Group B: ${res.data.sent_b}. Resolve in ~2 hours.`);
        fetchAll();
      } catch (e) { toast.error(e.response?.data?.detail || 'A/B test failed'); }
      finally { setSending(false); }
      return;
    }

    const count = filterType === 'custom_list' && !mergeEnabled ? customEmails.length : previewCount;
    const action = scheduleMode ? `Schedule for ${new Date(scheduleDatetime).toLocaleString()}` : `Send to ${count || '?'} recipients`;
    if (!confirm(`${action}?`)) return;
    try {
      setSending(true);
      const payload = buildPayload();
      const res = await axios.post(`${API_URL}/api/admin/email-campaigns/send`, payload, { headers });
      if (res.data.scheduled) {
        toast.success(`Campaign scheduled for ${new Date(scheduleDatetime).toLocaleString()} — ${res.data.total_recipients} recipients`);
        setScheduleMode(false); setScheduleDatetime('');
      } else {
        toast.success(`Campaign sent to ${res.data.sent_count} recipients!`);
      }
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSending(false); }
  };

  const saveTemplate = async () => {
    if (!templateName.trim()) { toast.error('Enter a template name'); return; }
    setSavingTemplate(true);
    try {
      await axios.post(`${API_URL}/api/admin/email-templates`, {
        name: templateName, subject, blocks: editorMode === 'visual' ? blocks : [],
        html_content: getHtml()
      }, { headers });
      toast.success('Template saved!');
      setShowSaveDialog(false);
      setTemplateName('');
      fetchAll();
    } catch { toast.error('Failed to save template'); }
    finally { setSavingTemplate(false); }
  };

  const deleteTemplate = async (id) => {
    if (!confirm('Delete this template?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/email-templates/${id}`, { headers });
      toast.success('Template deleted');
      fetchAll();
    } catch { toast.error('Failed to delete'); }
  };

  const switchMode = (mode) => {
    if (mode === 'html' && editorMode === 'visual') setRawHtml(blocksToHtml(blocks));
    setEditorMode(mode);
  };

  return (
    <div className="space-y-6" data-testid="campaigns-tab">
      {/* View Tabs */}
      <div className="flex gap-1 bg-neutral-100 rounded-lg p-1 w-fit">
        {[
          { key: 'compose', label: 'Compose', icon: Mail },
          { key: 'templates', label: 'Templates', icon: BookOpen },
          { key: 'followup', label: 'Auto Follow-Up', icon: Timer },
        ].map(v => (
          <button key={v.key} onClick={() => setActiveView(v.key)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${activeView === v.key ? 'bg-white shadow text-neutral-800' : 'text-neutral-500 hover:text-neutral-700'}`}
            data-testid={`tab-${v.key}`}>
            <v.icon className="w-4 h-4" /> {v.label}
          </button>
        ))}
      </div>

      {/* ════════ COMPOSE VIEW ════════ */}
      {activeView === 'compose' && (
        <>
          {/* Toggle Preview Panel */}
          <div className="flex justify-end mb-2">
            <Button variant="outline" size="sm" onClick={() => setShowPreview(p => !p)} className="text-xs gap-1.5" data-testid="toggle-preview-btn">
              {showPreview ? <PanelRightClose className="w-3.5 h-3.5" /> : <PanelLeftClose className="w-3.5 h-3.5" />}
              {showPreview ? 'Hide Preview' : 'Show Preview'}
            </Button>
          </div>

          <div ref={splitContainerRef} className="flex items-stretch" style={{ minHeight: '500px' }}>
            {/* Compose Form */}
            <div style={{ width: showPreview ? `${splitPct}%` : '100%' }} className="min-w-0 overflow-hidden">
          <Card className="h-full">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2"><Mail className="w-4 h-4 text-amber-600" /> Compose Campaign</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Templates */}
              <div>
                <Label className="text-xs text-neutral-500 uppercase tracking-wide">Quick Templates</Label>
                <div className="flex flex-wrap gap-2 mt-1.5">
                  {BUILT_IN_TEMPLATES.map(t => (
                    <Button key={t.id} variant="outline" size="sm" onClick={() => loadTemplate(t)} data-testid={`tpl-${t.id}`}>{t.name}</Button>
                  ))}
                  {savedTemplates.slice(0, 5).map(t => (
                    <Button key={t.id} variant="outline" size="sm" onClick={() => loadTemplate(t)} className="border-amber-200 text-amber-700" data-testid={`saved-tpl-${t.id}`}>{t.name}</Button>
                  ))}
                </div>
              </div>

              {/* Audience */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Audience</Label>
                  <select value={filterType} onChange={e => { setFilterType(e.target.value); setPreviewCount(null); setShowRecipients(false); }} className="w-full mt-1 rounded-md border border-neutral-300 p-2 text-sm" data-testid="campaign-filter">
                    {FILTER_OPTIONS.map(f => <option key={f.id} value={f.id}>{f.label} — {f.desc}</option>)}
                  </select>
                </div>
                {filterType !== 'custom_list' && (
                  <div>
                    <Label>Lookback (days)</Label>
                    <Input type="number" value={daysBack} onChange={e => setDaysBack(Number(e.target.value) || 90)} min={1} max={365} className="mt-1" data-testid="campaign-days" />
                  </div>
                )}
                {filterType === 'custom_list' && (
                  <div>
                    <Label>Saved Audiences</Label>
                    <select value={selectedAudienceId || ''} onChange={e => { const a = savedAudiences.find(x => x.id === e.target.value); if (a) loadAudience(a); else { setSelectedAudienceId(null); setAudienceName(''); } }} className="w-full mt-1 rounded-md border border-neutral-300 p-2 text-sm" data-testid="audience-select">
                      <option value="">— New list —</option>
                      {savedAudiences.map(a => <option key={a.id} value={a.id}>{a.name} ({a.count})</option>)}
                    </select>
                  </div>
                )}
              </div>

              {/* Custom Email List Input */}
              {filterType === 'custom_list' && (
                <div className="space-y-2" data-testid="custom-audience-panel">
                  <div className="flex gap-2">
                    <Input value={emailInput} onChange={e => setEmailInput(e.target.value)} placeholder="Paste emails (comma, space, or newline separated)" className="flex-1 text-sm" data-testid="email-input" onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addEmails(emailInput); } }} />
                    <Button variant="outline" size="sm" onClick={() => addEmails(emailInput)} disabled={!emailInput.trim()} data-testid="add-emails-btn">Add</Button>
                    <input ref={csvFileRef} type="file" accept=".csv,.txt,.tsv,.xlsx" className="hidden" onChange={handleCsvImport} data-testid="csv-file-input" />
                    <Button variant="outline" size="sm" onClick={() => csvFileRef.current?.click()} className="gap-1.5 text-neutral-600" data-testid="csv-import-btn"><Upload className="w-3.5 h-3.5" /> CSV</Button>
                  </div>
                  {customEmails.length > 0 && (
                    <div className="bg-neutral-50 rounded-lg p-2.5 max-h-36 overflow-y-auto">
                      <div className="flex flex-wrap gap-1">
                        {customEmails.map(e => (
                          <span key={e} className="inline-flex items-center gap-1 text-xs bg-white border rounded-full px-2 py-0.5 group" data-testid={`email-tag-${e}`}>
                            {e}
                            <button onClick={() => removeEmail(e)} className="text-neutral-300 hover:text-red-500 transition-colors" data-testid={`remove-email-${e}`}>&times;</button>
                          </span>
                        ))}
                      </div>
                      <div className="flex items-center justify-between mt-2 pt-2 border-t border-neutral-200">
                        <span className="text-xs text-neutral-500 font-medium" data-testid="custom-email-count">{customEmails.length} recipient{customEmails.length !== 1 ? 's' : ''}</span>
                        <div className="flex gap-2">
                          <button onClick={() => { setCustomEmails([]); setSelectedAudienceId(null); setAudienceName(''); }} className="text-xs text-neutral-400 hover:text-red-500">Clear all</button>
                          <button onClick={() => setShowAudienceSave(true)} className="text-xs text-amber-600 hover:text-amber-700 font-medium" data-testid="save-audience-btn">{selectedAudienceId ? 'Update' : 'Save'} Audience</button>
                        </div>
                      </div>
                    </div>
                  )}
                  {/* Save audience dialog */}
                  {showAudienceSave && (
                    <div className="flex gap-2 items-end bg-amber-50 p-2.5 rounded-lg border border-amber-100" data-testid="save-audience-dialog">
                      <div className="flex-1">
                        <Label className="text-xs">Audience Name</Label>
                        <Input value={audienceName} onChange={e => setAudienceName(e.target.value)} placeholder="e.g. VIP Clients" className="mt-1 text-sm" data-testid="audience-name-input" />
                      </div>
                      <Button size="sm" onClick={saveAudience} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="confirm-save-audience">{selectedAudienceId ? 'Update' : 'Save'}</Button>
                      <Button size="sm" variant="ghost" onClick={() => setShowAudienceSave(false)}>Cancel</Button>
                    </div>
                  )}
                  {/* Saved audiences quick list */}
                  {savedAudiences.length > 0 && !selectedAudienceId && (
                    <div className="flex flex-wrap gap-1.5">
                      {savedAudiences.map(a => (
                        <div key={a.id} className="inline-flex items-center gap-1 border rounded-full px-2 py-0.5 text-xs hover:border-amber-300 transition-colors">
                          <button onClick={() => loadAudience(a)} className="text-neutral-600 hover:text-amber-700" data-testid={`load-audience-${a.id}`}>{a.name} <span className="text-neutral-400">({a.count})</span></button>
                          <button onClick={() => deleteAudience(a.id)} className="text-neutral-300 hover:text-red-500 ml-0.5" data-testid={`delete-audience-${a.id}`}>&times;</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="flex items-center gap-3">
                <Button variant="outline" size="sm" onClick={previewAudience} disabled={sending || (filterType === 'custom_list' && !mergeEnabled && !customEmails.length)} data-testid="preview-audience-btn">
                  <Users className="w-4 h-4 mr-1" /> {filterType === 'custom_list' && !mergeEnabled ? `Preview (${customEmails.length})` : 'Preview Audience'}
                </Button>
                {previewCount !== null && <span className="text-sm font-medium text-amber-700 bg-amber-50 px-3 py-1 rounded-full" data-testid="recipient-count">{previewCount} recipients</span>}
                {/* Audience Merge Toggle */}
                <label className="flex items-center gap-1.5 ml-auto cursor-pointer" data-testid="merge-toggle">
                  <input type="checkbox" checked={mergeEnabled} onChange={e => { setMergeEnabled(e.target.checked); if (e.target.checked && !mergeFilters.length) setMergeFilters([filterType]); }} className="rounded border-neutral-300 text-amber-600 focus:ring-amber-500" />
                  <span className="text-xs text-neutral-500">Merge audiences</span>
                </label>
              </div>
              {/* Merge filters picker */}
              {mergeEnabled && (
                <div className="flex flex-wrap items-center gap-2 p-2.5 bg-blue-50 rounded-lg border border-blue-100" data-testid="merge-panel">
                  <span className="text-xs text-blue-700 font-medium shrink-0">Combine:</span>
                  {FILTER_OPTIONS.map(f => (
                    <label key={f.id} className="flex items-center gap-1 cursor-pointer">
                      <input type="checkbox" checked={mergeFilters.includes(f.id)} onChange={e => { if (e.target.checked) setMergeFilters(prev => [...new Set([...prev, f.id])]); else setMergeFilters(prev => prev.filter(x => x !== f.id)); }} className="rounded border-neutral-300 text-blue-600 focus:ring-blue-500" />
                      <span className="text-xs text-neutral-600">{f.label}</span>
                    </label>
                  ))}
                  {mergeFilters.includes('custom_list') && !customEmails.length && <span className="text-[10px] text-orange-500">Add emails to custom list above</span>}
                </div>
              )}
              {showRecipients && previewRecipients.length > 0 && filterType !== 'custom_list' && (
                <div className="bg-neutral-50 rounded-lg p-3 max-h-32 overflow-y-auto">
                  <div className="flex flex-wrap gap-1">{previewRecipients.map(e => <span key={e} className="text-xs bg-white border rounded px-1.5 py-0.5">{e}</span>)}</div>
                </div>
              )}

              {/* Subject + A/B Toggle */}
              <div>
                <div className="flex items-center justify-between">
                  <Label>Subject Line</Label>
                  <button onClick={() => setAbMode(!abMode)} className={`flex items-center gap-1 text-xs font-medium transition-colors ${abMode ? 'text-purple-600' : 'text-neutral-400 hover:text-neutral-600'}`} data-testid="toggle-ab">
                    <FlaskConical className="w-3.5 h-3.5" /> A/B Test
                  </button>
                </div>
                <Input value={subject} onChange={e => setSubject(e.target.value)} placeholder="Subject A" className="mt-1" data-testid="campaign-subject" />
                {abMode && (
                  <div className="mt-2 p-3 bg-purple-50 rounded-lg border border-purple-200 space-y-2">
                    <div className="flex items-center gap-2">
                      <Label className="text-xs text-purple-700 shrink-0">Subject B</Label>
                      <Input value={subjectB} onChange={e => setSubjectB(e.target.value)} placeholder="Alternative subject line" className="text-sm" data-testid="subject-b" />
                    </div>
                    <div className="flex items-center gap-2">
                      <Label className="text-xs text-purple-700 shrink-0">Test group</Label>
                      <Input type="number" value={testPct} onChange={e => setTestPct(Number(e.target.value))} min={10} max={50} className="w-20 text-sm" />
                      <span className="text-xs text-purple-500">% of audience</span>
                    </div>
                    <p className="text-[10px] text-purple-400">Each subject gets {testPct/2}% of audience. After ~2 hours, pick the winner to send to the rest.</p>
                  </div>
                )}
              </div>

              {/* Editor */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Email Body</Label>
                  <div className="flex items-center gap-2">
                    <div className="flex bg-neutral-100 rounded-md p-0.5">
                      <button onClick={() => switchMode('visual')} className={`px-3 py-1 text-xs rounded font-medium transition-colors ${editorMode === 'visual' ? 'bg-white shadow text-neutral-800' : 'text-neutral-500'}`} data-testid="mode-visual">Visual</button>
                      <button onClick={() => switchMode('html')} className={`px-3 py-1 text-xs rounded font-medium transition-colors ${editorMode === 'html' ? 'bg-white shadow text-neutral-800' : 'text-neutral-500'}`} data-testid="mode-html">HTML</button>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => setShowSaveDialog(true)} className="text-xs text-neutral-500" data-testid="save-template-btn">
                      <Save className="w-3.5 h-3.5 mr-1" /> Save as Template
                    </Button>
                  </div>
                </div>
                {editorMode === 'visual' ? (
                  <div ref={editorRef} className="border rounded-lg p-3 bg-neutral-50/50 overflow-auto resize-y" style={{ height: editorHeight, minHeight: '150px' }}><EmailBlockEditor blocks={blocks} onChange={setBlocks} onUndo={undo} onRedo={redo} canUndo={canUndo} canRedo={canRedo} /></div>
                ) : (
                  <textarea ref={editorRef} value={rawHtml} onChange={e => setRawHtml(e.target.value)} className="w-full rounded-md border p-3 text-sm font-mono resize-y" placeholder="Paste email HTML here..." data-testid="campaign-html" style={{ height: editorHeight, minHeight: '150px' }} />
                )}
              </div>

              {/* Save Template Dialog */}
              {showSaveDialog && (
                <div className="flex items-center gap-2 p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <Input value={templateName} onChange={e => setTemplateName(e.target.value)} placeholder="Template name..." className="text-sm flex-1" data-testid="template-name-input" autoFocus />
                  <Button size="sm" onClick={saveTemplate} disabled={savingTemplate} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="save-template-confirm">
                    {savingTemplate ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setShowSaveDialog(false)}>Cancel</Button>
                </div>
              )}

              {/* Preview - removed, now using the Live Preview panel instead */}

              {/* Schedule Toggle + Actions */}
              <div className="space-y-3 pt-2 border-t">
                {/* Schedule bar */}
                <div className="flex items-center gap-3 flex-wrap">
                  <label className="flex items-center gap-1.5 cursor-pointer" data-testid="schedule-toggle">
                    <input type="checkbox" checked={scheduleMode} onChange={e => setScheduleMode(e.target.checked)} className="rounded border-neutral-300 text-amber-600 focus:ring-amber-500" />
                    <Clock className="w-3.5 h-3.5 text-neutral-400" />
                    <span className="text-xs text-neutral-600 font-medium">Schedule for later</span>
                  </label>
                  {scheduleMode && (
                    <input type="datetime-local" value={scheduleDatetime} onChange={e => setScheduleDatetime(e.target.value)} min={new Date().toISOString().slice(0, 16)} className="text-sm border rounded-md px-2 py-1 focus:ring-1 focus:ring-amber-300 outline-none" data-testid="schedule-datetime" />
                  )}
                  {/* Optimal Send Time */}
                  <button onClick={fetchBestSendTime} disabled={loadingSendTime} className="ml-auto flex items-center gap-1 text-xs text-violet-600 hover:text-violet-800 font-medium transition-colors" data-testid="optimize-send-time-btn">
                    {loadingSendTime ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                    Best Send Time
                  </button>
                </div>
                {/* Send Time Suggestion */}
                {sendTimeSuggestion && (
                  <div className="flex items-center gap-3 p-2.5 bg-violet-50 rounded-lg border border-violet-200" data-testid="send-time-suggestion">
                    <Sparkles className="w-4 h-4 text-violet-500 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-violet-800">{sendTimeSuggestion.suggestion}</p>
                      <p className="text-[10px] text-violet-500">{sendTimeSuggestion.reason}{sendTimeSuggestion.total_opens_analyzed ? ` (${sendTimeSuggestion.total_opens_analyzed} opens analyzed)` : ''}</p>
                    </div>
                    {scheduleMode && (
                      <Button size="sm" variant="outline" className="text-xs border-violet-300 text-violet-700 shrink-0" data-testid="apply-send-time" onClick={() => {
                        const dayMap = { Sunday: 0, Monday: 1, Tuesday: 2, Wednesday: 3, Thursday: 4, Friday: 5, Saturday: 6 };
                        const targetDay = dayMap[sendTimeSuggestion.best_day] ?? 2;
                        const targetHour = sendTimeSuggestion.best_hour ?? 10;
                        const now = new Date();
                        const daysUntil = (targetDay - now.getDay() + 7) % 7 || 7;
                        const target = new Date(now.getFullYear(), now.getMonth(), now.getDate() + daysUntil, targetHour, 0);
                        if (target <= now) target.setDate(target.getDate() + 7);
                        const pad = n => String(n).padStart(2, '0');
                        setScheduleDatetime(`${target.getFullYear()}-${pad(target.getMonth()+1)}-${pad(target.getDate())}T${pad(target.getHours())}:${pad(target.getMinutes())}`);
                        toast.success('Applied suggested send time');
                      }}>
                        Apply
                      </Button>
                    )}
                    <button onClick={() => setSendTimeSuggestion(null)} className="text-violet-300 hover:text-violet-500 p-0.5"><X className="w-3 h-3" /></button>
                  </div>
                )}
                {/* Send buttons */}
                <div className="flex items-center gap-3">
                  <Button variant="outline" onClick={sendPreview} disabled={sending || !subject} data-testid="send-preview-btn">
                    {sending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Eye className="w-4 h-4 mr-1" />} Send Preview
                  </Button>
                  <Button onClick={sendCampaign} disabled={sending || !subject || (!abMode && !scheduleMode && previewCount === null)} className={abMode ? 'bg-purple-600 hover:bg-purple-700' : scheduleMode ? 'bg-blue-600 hover:bg-blue-700' : 'bg-amber-600 hover:bg-amber-700'} data-testid="send-campaign-btn">
                    {sending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : scheduleMode ? <CalIcon className="w-4 h-4 mr-1" /> : abMode ? <FlaskConical className="w-4 h-4 mr-1" /> : <Send className="w-4 h-4 mr-1" />}
                    {abMode ? 'Start A/B Test' : scheduleMode ? 'Schedule Campaign' : `Send to ${previewCount || '?'}`}
                  </Button>
                  {!abMode && !scheduleMode && previewCount === null && <span className="text-xs text-neutral-400 flex items-center gap-1"><AlertCircle className="w-3 h-3" /> Preview audience first</span>}
                </div>
              </div>
            </CardContent>
          </Card>
          </div>

          {/* Draggable Split Divider */}
          {showPreview && (
            <div
              onMouseDown={handleSplitDragStart}
              onTouchStart={handleSplitDragStart}
              className="w-2 cursor-col-resize flex-shrink-0 flex items-center justify-center group hover:bg-amber-100 transition-colors rounded"
              data-testid="split-divider"
              title="Drag to resize"
            >
              <div className="w-0.5 h-8 bg-neutral-300 group-hover:bg-amber-500 rounded-full transition-colors" />
            </div>
          )}

          {/* Device Preview Panel */}
          {showPreview && (
            <div className="min-w-0 overflow-hidden" style={{ width: `${100 - splitPct}%` }} data-testid="email-preview-panel">
              <div className="h-full border rounded-lg overflow-hidden bg-white shadow-sm flex flex-col">
                {/* Device Switcher */}
                <div className="bg-neutral-900 text-white px-3 py-2 flex items-center justify-between shrink-0">
                  <span className="text-xs font-medium">Live Preview</span>
                  <div className="flex gap-1">
                    {[
                      { key: 'mobile', icon: Smartphone, label: 'Mobile' },
                      { key: 'tablet', icon: Tablet, label: 'Tablet' },
                      { key: 'desktop', icon: Monitor, label: 'Desktop' },
                    ].map(d => (
                      <button
                        key={d.key}
                        onClick={() => setPreviewDevice(d.key)}
                        className={`p-1.5 rounded transition-colors ${previewDevice === d.key ? 'bg-white/20 text-white' : 'text-neutral-400 hover:text-white'}`}
                        title={d.label}
                        data-testid={`preview-${d.key}`}
                      >
                        <d.icon className="w-3.5 h-3.5" />
                      </button>
                    ))}
                  </div>
                </div>

                {/* Preview Frame */}
                <div className="flex-1 p-3 bg-neutral-100 flex justify-center overflow-auto">
                  <div
                    className="bg-white rounded-lg shadow-inner overflow-hidden transition-all duration-300 self-start"
                    style={{
                      width: previewDevice === 'mobile' ? '320px' : previewDevice === 'tablet' ? '480px' : '100%',
                      maxWidth: previewDevice === 'mobile' ? '320px' : previewDevice === 'tablet' ? '480px' : '100%',
                      border: previewDevice !== 'desktop' ? '8px solid #1a1a1a' : 'none',
                      borderRadius: previewDevice !== 'desktop' ? '16px' : '8px',
                    }}
                    data-testid="preview-frame"
                  >
                    <div className="px-3 py-2 border-b bg-neutral-50">
                      <p className="text-[10px] text-neutral-400">Subject:</p>
                      <p className="text-xs font-medium text-neutral-700 truncate">{subject || 'No subject'}</p>
                    </div>
                    <div
                      className="overflow-y-auto text-sm"
                      style={{ fontSize: previewDevice === 'mobile' ? '13px' : '14px' }}
                      dangerouslySetInnerHTML={{ __html: getHtml() }}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
          </div>

          {/* Past Campaigns */}
          {campaigns.length > 0 && (
            <Card>
              <CardHeader><CardTitle className="text-sm flex items-center gap-2"><History className="w-4 h-4" /> Past Campaigns</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                {campaigns.map(c => c.type === 'ab_test' ? (
                  <ABTestCard key={c.id} test={c} token={token} onRefresh={fetchAll} />
                ) : (
                  <div key={c.id} className="p-3 bg-neutral-50 rounded-lg" data-testid={`campaign-row-${c.id}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-sm">{c.subject}</p>
                        <p className="text-xs text-neutral-500">
                          {new Date(c.created_at).toLocaleDateString()} · {c.filter_type} · {c.status === 'scheduled' && c.scheduled_at ? `Scheduled for ${new Date(c.scheduled_at).toLocaleString()}` : `Last ${c.days_back} days`}
                        </p>
                      </div>
                      <div className="flex items-center gap-3 text-xs">
                        {c.status === 'scheduled' ? (
                          <span className="flex items-center gap-1 text-blue-600" data-testid="campaign-scheduled-badge"><Clock className="w-3.5 h-3.5" /> Scheduled</span>
                        ) : c.status === 'cancelled' ? (
                          <span className="flex items-center gap-1 text-neutral-400"><Ban className="w-3.5 h-3.5" /> Cancelled</span>
                        ) : (
                          <span className="flex items-center gap-1 text-green-600"><CheckCircle className="w-3.5 h-3.5" /> {c.sent_count} sent</span>
                        )}
                        {c.failed_count > 0 && <span className="text-red-500">{c.failed_count} failed</span>}
                        <button onClick={() => duplicateCampaign(c.id)} className="flex items-center gap-1 text-xs text-amber-600 hover:text-amber-800 transition-colors" data-testid={`duplicate-campaign-${c.id}`}>
                          <Copy className="w-3 h-3" /> Duplicate
                        </button>
                        {c.status === 'scheduled' && (
                          <button onClick={async () => { if (!confirm('Cancel this scheduled campaign?')) return; try { await axios.delete(`${API_URL}/api/admin/scheduled-campaigns/${c.id}`, { headers }); toast.success('Campaign cancelled'); fetchAll(); } catch { toast.error('Failed to cancel'); } }} className="text-xs text-red-400 hover:text-red-600" data-testid={`cancel-campaign-${c.id}`}>Cancel</button>
                        )}
                      </div>
                    </div>
                    {c.status !== 'scheduled' && c.status !== 'cancelled' && <div className="mt-2"><CampaignStats campaignId={c.id} token={token} /></div>}
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* ════════ TEMPLATES VIEW ════════ */}
      {activeView === 'templates' && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2"><BookOpen className="w-5 h-5 text-amber-600" /> Email Template Library</CardTitle>
            <CardDescription>Saved email compositions you can reuse in any campaign</CardDescription>
          </CardHeader>
          <CardContent>
            {savedTemplates.length === 0 ? (
              <div className="text-center py-8 text-neutral-400">
                <BookOpen className="w-10 h-10 mx-auto mb-2 opacity-40" />
                <p className="text-sm">No saved templates yet</p>
                <p className="text-xs mt-1">Create one from the Compose tab using "Save as Template"</p>
              </div>
            ) : (
              <div className="space-y-2" data-testid="templates-list">
                {savedTemplates.map(t => (
                  <div key={t.id} className="flex items-center justify-between p-3 bg-neutral-50 rounded-lg border hover:bg-neutral-100 transition-colors">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm">{t.name}</p>
                      <p className="text-xs text-neutral-500 truncate">{t.subject}</p>
                      <p className="text-[10px] text-neutral-400">{new Date(t.created_at).toLocaleDateString()} · {t.created_by}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0 ml-3">
                      <Button variant="outline" size="sm" onClick={() => { loadTemplate(t); setActiveView('compose'); }} data-testid={`use-tpl-${t.id}`}>Use</Button>
                      <button onClick={() => deleteTemplate(t.id)} className="p-1.5 text-neutral-300 hover:text-red-500" data-testid={`del-tpl-${t.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* ════════ AUTO FOLLOW-UP VIEW ════════ */}
      {activeView === 'followup' && (
        <FollowUpEditor
          token={token}
          sequence={followUpSeq}
          log={followUpLog}
          onSave={(seq) => { setFollowUpSeq(seq); fetchAll(); }}
        />
      )}
    </div>
  );
};

/* ─── Follow-Up Sequence Editor ─── */
function FollowUpEditor({ token, sequence, log, onSave }) {
  const [steps, setSteps] = useState(() => {
    if (sequence?.delays_days?.length) {
      return sequence.delays_days.map((d, i) => ({
        delay: d,
        subject: sequence.subjects?.[i] || '',
        html: sequence.html_contents?.[i] || '',
        blocks: sequence.blocks_list?.[i] || [],
      }));
    }
    return [
      { delay: 3, subject: 'Apollo Shades — Your shade quote is ready', html: '', blocks: [] },
      { delay: 7, subject: 'Apollo Shades — Still thinking about those shades?', html: '', blocks: [] },
      { delay: 14, subject: 'Apollo Shades — Last chance: 10% off your order', html: '', blocks: [] },
    ];
  });
  const [enabled, setEnabled] = useState(sequence?.enabled ?? true);
  const [saving, setSaving] = useState(false);
  const [editingStep, setEditingStep] = useState(null);
  const [stepBlocks, setStepBlocks] = useState([]);
  const headers = { Authorization: `Bearer ${token}` };

  const updateStep = (i, field, value) => {
    setSteps(prev => prev.map((s, idx) => idx === i ? { ...s, [field]: value } : s));
  };

  const addStep = () => {
    const lastDelay = steps[steps.length - 1]?.delay || 7;
    setSteps(prev => [...prev, { delay: lastDelay + 7, subject: '', html: '', blocks: [] }]);
  };

  const removeStep = (i) => setSteps(prev => prev.filter((_, idx) => idx !== i));

  const openStepEditor = (i) => {
    setEditingStep(i);
    setStepBlocks(steps[i].blocks?.length ? steps[i].blocks : templateToBlocks('followup'));
  };

  const saveStepContent = () => {
    const html = blocksToHtml(stepBlocks);
    updateStep(editingStep, 'html', html);
    updateStep(editingStep, 'blocks', stepBlocks);
    setEditingStep(null);
  };

  const saveSequence = async () => {
    const valid = steps.every(s => s.subject && s.delay > 0);
    if (!valid) { toast.error('Fill in all step subjects and delays'); return; }
    // Auto-generate HTML for steps without content
    const finalSteps = steps.map(s => ({
      ...s,
      html: s.html || blocksToHtml(templateToBlocks('followup'))
    }));
    setSaving(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/follow-up-sequences`, {
        name: 'Quote Follow-Up Sequence',
        delays_days: finalSteps.map(s => s.delay),
        subjects: finalSteps.map(s => s.subject),
        html_contents: finalSteps.map(s => s.html),
        blocks_list: finalSteps.map(s => s.blocks || []),
        enabled
      }, { headers });
      toast.success('Follow-up sequence saved!');
      onSave?.(res.data);
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg flex items-center gap-2"><Timer className="w-5 h-5 text-amber-600" /> Auto Follow-Up Sequence</CardTitle>
              <CardDescription>Automatically email new quote leads on Day 3, 7, 14 (customizable)</CardDescription>
            </div>
            <button onClick={() => setEnabled(!enabled)} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${enabled ? 'bg-green-100 text-green-700' : 'bg-neutral-100 text-neutral-500'}`} data-testid="toggle-sequence">
              {enabled ? <Play className="w-3 h-3" /> : <Pause className="w-3 h-3" />}
              {enabled ? 'Active' : 'Paused'}
            </button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Step editor overlay */}
          {editingStep !== null && (
            <div className="border rounded-lg p-4 bg-amber-50 space-y-3">
              <div className="flex items-center justify-between">
                <p className="font-medium text-sm">Editing Step {editingStep + 1} — Day {steps[editingStep]?.delay}</p>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => setEditingStep(null)}>Cancel</Button>
                  <Button size="sm" onClick={saveStepContent} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="save-step-content">Apply</Button>
                </div>
              </div>
              <div className="border rounded-lg p-3 bg-white">
                <EmailBlockEditor blocks={stepBlocks} onChange={setStepBlocks} />
              </div>
            </div>
          )}

          {editingStep === null && (
            <>
              {/* Steps list */}
              <div className="space-y-2" data-testid="followup-steps">
                {steps.map((step, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 bg-neutral-50 rounded-lg border">
                    <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center shrink-0">
                      <span className="text-xs font-bold text-amber-700">D{step.delay}</span>
                    </div>
                    <div className="flex-1 min-w-0 space-y-1">
                      <div className="flex items-center gap-2">
                        <Label className="text-xs text-neutral-400 shrink-0 w-10">Day</Label>
                        <Input type="number" value={step.delay} onChange={e => updateStep(i, 'delay', Number(e.target.value))} className="w-16 h-7 text-sm" min={1} data-testid={`step-delay-${i}`} />
                      </div>
                      <Input value={step.subject} onChange={e => updateStep(i, 'subject', e.target.value)} placeholder="Email subject..." className="h-7 text-sm" data-testid={`step-subject-${i}`} />
                    </div>
                    <Button variant="outline" size="sm" onClick={() => openStepEditor(i)} className="text-xs shrink-0" data-testid={`edit-step-${i}`}>Edit Body</Button>
                    {steps.length > 1 && (
                      <button onClick={() => removeStep(i)} className="p-1 text-neutral-300 hover:text-red-500"><X className="w-4 h-4" /></button>
                    )}
                  </div>
                ))}
              </div>

              <div className="flex items-center gap-3">
                <Button variant="outline" size="sm" onClick={addStep} data-testid="add-step"><Plus className="w-4 h-4 mr-1" /> Add Step</Button>
                <Button onClick={saveSequence} disabled={saving} className="bg-amber-600 hover:bg-amber-700" data-testid="save-sequence">
                  {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Save Sequence
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Follow-up Log */}
      {log.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-sm flex items-center gap-2"><History className="w-4 h-4" /> Recent Follow-Up Emails</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-1 max-h-48 overflow-y-auto" data-testid="followup-log">
              {log.slice(0, 30).map((l, i) => (
                <div key={i} className="flex items-center justify-between text-xs py-1.5 border-b border-neutral-100">
                  <span className="text-neutral-600">{l.email}</span>
                  <span className="text-neutral-400">Day {l.delay_days} · {new Date(l.sent_at).toLocaleDateString()}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default CampaignsTab;
